import { IoChevronForwardCircleOutline } from "react-icons/io5";
import { LuCalendarDays, LuPackageSearch } from "react-icons/lu";
import { BiReset } from "react-icons/bi";
import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RxCrossCircled } from "react-icons/rx";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import "../../../../styles.css";
import "../../../../slicer-styles.css";
import RadioDropdown from "../../../UI/RadioDropdown/RadioDropdown";
import { RootState } from "../../../../store";
import { promotionPlanningTabActions } from "../../../../store/slices/promotion-planning-tab-slice";
import { tradeAndPromoPlanningTabActions } from "../../../../store/slices/trade-and-promo-planning-tab-slice";
import { PROMOTION_PLANNING_TAB_ID, TOAST_CONTAINER_ID } from "../../../../shared/constants";
import CheckboxDropdown from "../../../UI/CheckboxDropdown/CheckboxDropdown";
import { PromotionPlanningDropdownModel, PromotionPlanningSelectedSlicerModel } from "../../../../models/TradeAndPromoPlanning/PromotionPlanningSlicer.model";
import { getDefaultSlicersForForecastEntryApp, getToastOptions } from "../../../../shared/functions";
import Modal from "../../../UI/Modal/Modal";

const PromotionPlanningSlicers = (props: {
    isGridChanged: boolean;
}) => {
    // States
    const [isSlicerChanged, setIsSlicerChanged] = useState<boolean>(false);
    const [slicerChangeAction, setSlicerChangeAction] = useState<"apply" | "reset">("apply");

    // Variables
    const modalCloseBtnRef = useRef<HTMLButtonElement>(null);
    const slicerChangeModalId: string = "promotionPlanningSlicerChangeModal";

    // Redux variables
    const dispatchFn = useDispatch();
    const slicers: PromotionPlanningDropdownModel = useSelector((state: RootState) => state.promotionPlanning.slicers);
    const tempSelectedSlicers: PromotionPlanningSelectedSlicerModel = useSelector((state: RootState) => state.promotionPlanning.tempSelectedSlicers);

    /**
     * Handler for applying selected slicers.
     * @param {Event} event Apply filters button click event.
     * @returns {void}
     */
    const applySelectedSlicers = (event: any) => {
        const tempSlicers: any = { ...tempSelectedSlicers };
        const selectedSlicers: PromotionPlanningSelectedSlicerModel = {
            sku_ids: (
                tempSlicers.sku_ids.includes("All") ?
                    ["All"] : tempSlicers.sku_ids.map((v: string) => Number(v))
            ),
            year: tempSelectedSlicers.year
        };

        dispatchFn(promotionPlanningTabActions.setSelectedSlicers(selectedSlicers));
        dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
            [PROMOTION_PLANNING_TAB_ID]: false
        }));
        setIsSlicerChanged(false);

        if (props.isGridChanged) {
            modalCloseBtnRef.current?.click();
        }
    };

    /**
     * Handler for resetting selected slicers.
     * @param {Event} event Reset filters button click event.
     * @returns {void}
     */
    const resetSelectedSlicers = (event: any) => {
        const defaultSlicers: PromotionPlanningSelectedSlicerModel = getDefaultSlicersForForecastEntryApp(PROMOTION_PLANNING_TAB_ID);

        if (
            tempSelectedSlicers.sku_ids.toSorted().toString() !== defaultSlicers.sku_ids.toSorted().toString() ||
            tempSelectedSlicers.year !== defaultSlicers.year
        ) {
            dispatchFn(promotionPlanningTabActions.resetSelectedSlicers(null));
            dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                [PROMOTION_PLANNING_TAB_ID]: false
            }));
            setIsSlicerChanged(false);
        } else {
            toast.info("No filters have been changed to reset", {
                ...getToastOptions("info"),
                containerId: TOAST_CONTAINER_ID
            });
        }

        if (props.isGridChanged) {
            modalCloseBtnRef.current?.click();
        }
    };

    /**
     * Event handler for dropdown value change.
     * @param {string} field Slicer field which was updated.
     * @param {string | string[]} value Updated slicer value(s).
     */
    const dropdownItemChangeHandler = (field: string, value: string | string[]) => {
        if (field === "sku_ids") {
            dispatchFn(promotionPlanningTabActions.setTempSlicers({
                ...tempSelectedSlicers,
                [field]: value
            }));
        } else {
            dispatchFn(promotionPlanningTabActions.setTempSlicers({
                ...tempSelectedSlicers,
                [field]: Number(value)
            }));
        }
        setIsSlicerChanged(true);
    };

    /**
     * Handler to close slicer change modal
     */
    const closeSlicerChangeModal = (event: any) => {
        modalCloseBtnRef.current?.click();
    };

    // Slicer Change Modal body
    const slicerChangeModalBody: JSX.Element = (
        <div className="row">
            <div className="col col-12 mb-2">
                {`${(slicerChangeAction === "apply") ? "Applying" : "Resetting"} filters will discard the changes done to the grid. Do you want to proceed?`}
            </div>
            <div className="col col-6">
                <button className="btn btn-warning w-100" onClick={closeSlicerChangeModal}>
                    <span className="fs-5">
                        <RxCrossCircled />
                    </span> No
                </button>
            </div>
            <div className="col col-6">
                <button className="btn btn-success w-100" onClick={slicerChangeAction === "apply" ? applySelectedSlicers : resetSelectedSlicers}>
                    <span className="fs-5">
                        <IoIosCheckmarkCircleOutline />
                    </span> Yes
                </button>
            </div>
        </div>
    );

    return (
        <>
            <CheckboxDropdown
                id="promotion-planning-sku-dropdown"
                icon={<LuPackageSearch size={20} />}
                label="Product(s)"
                selectedValues={tempSelectedSlicers.sku_ids.map(id => id.toString())}
                items={slicers.sku}
                onItemChanged={(selectedValues: string[]) => dropdownItemChangeHandler("sku_ids", selectedValues)}
            />

            <RadioDropdown
                id="promotion-planning-year-dropdown"
                icon={<LuCalendarDays size={20} />}
                label="Year"
                selectedValue={tempSelectedSlicers.year.toString()}
                items={slicers.years}
                onItemChanged={(value: any) => dropdownItemChangeHandler("year", value)}
            />

            <div className="d-inline me-1">
                <button type="button" className="btn btn-sm text-white" style={{ backgroundColor: "#00833E" }} onClick={
                    (e) => props.isGridChanged ? setSlicerChangeAction("apply") : applySelectedSlicers(e)
                } disabled={!isSlicerChanged} {...(props.isGridChanged ? { "data-bs-toggle": "modal", "data-bs-target": `#${slicerChangeModalId}` } : {})}>
                    <div className="d-flex justify-content-center align-items-center">
                        <IoChevronForwardCircleOutline size={20} /> &nbsp;Apply filters
                    </div>
                </button>
            </div>

            <div className="d-inline">
                <button type="button" className="btn btn-sm btn-warning" onClick={
                    (e) => props.isGridChanged ? setSlicerChangeAction("reset") : resetSelectedSlicers(e)
                } {...(props.isGridChanged ? { "data-bs-toggle": "modal", "data-bs-target": `#${slicerChangeModalId}` } : {})}>
                    <div className="d-flex justify-content-center align-items-center">
                        <BiReset size={20} /> &nbsp;Reset filters
                    </div>
                </button>
            </div>

            {
                createPortal(
                    <Modal
                        id={slicerChangeModalId}
                        ref={modalCloseBtnRef}
                        title={`${(slicerChangeAction === "apply") ? "Apply" : "Reset"} Filters?`}
                        onModalClose={() => { }}
                    >
                        {slicerChangeModalBody}
                    </Modal>,
                    document.getElementById("modal-root") as HTMLElement
                )
            }
        </>
    );
};

export default PromotionPlanningSlicers;
